/**
 * ============================================================================
 * ChangeControlRegister — Quality Events / Change Control (Phase 6c).
 * Route: /changes
 *
 * The change-control register/queue that was missing: lists all
 * cc_impact.change_requests, each row opening the impact-set review
 * (/changes/$id/impact-set). The "Propose change" action creates a new CR
 * (/changes/new). This makes Change Control a first-class Quality-Events module
 * instead of a lone "Propose Change" action buried in the metrics group.
 * ============================================================================ */

import { useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { PageHero, Button } from '@edms/design-system';
import wstyles from '../WebhookAdmin/WebhookAdmin.module.css';
import styles from './ChangeControlRegister.module.css';
import { useChangeRequestsQuery, type ChangeRequestItem } from './useChangeRequestsQuery';

function statusClass(status: string): string {
  const s = status.toUpperCase();
  if (s === 'DRAFT') return styles.statusDraft;
  if (s.includes('REVIEW') || s.includes('PENDING')) return styles.statusReview;
  if (s.includes('READY') || s.includes('IMPACT')) return styles.statusReady;
  if (s.includes('APPROV')) return styles.statusApproved;
  if (s.includes('CLOSED') || s.includes('IMPLEMENT') || s.includes('DONE')) return styles.statusClosed;
  return styles.statusDraft;
}

export function ChangeControlRegister() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const q = useChangeRequestsQuery();
  const items: ChangeRequestItem[] = q.data?.items ?? [];

  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }),
    [i18n.language],
  );

  function open(cr: ChangeRequestItem) {
    navigate({ to: '/changes/$id/impact-set', params: { id: cr.change_request_id } });
  }

  return (
    <div className={wstyles.adminScreen}>
      <PageHero
        eyebrow={t('cc.register.eyebrow', { defaultValue: 'Quality Events' })}
        title={t('cc.register.title', { defaultValue: 'Change control' })}
        subtitle={t('cc.register.subtitle', {
          defaultValue:
            'Change requests with their impact set, assessment, approval, and post-action verification (GMP change control).',
        })}
        actions={
          <Button variant="primary" leadingIcon="+" onClick={() => navigate({ to: '/changes/new' })}>
            {t('cc.register.propose', { defaultValue: 'Propose change' })}
          </Button>
        }
      />

      {q.isLoading && (
        <div>{[0, 1, 2, 3].map((i) => <div key={i} className={styles.skeletonRow} />)}</div>
      )}

      {q.isError && (
        <div className={styles.errorEnvelope} role="alert">
          <p>{t('cc.register.error', { defaultValue: 'Could not load change requests.' })}</p>
          <Button variant="secondary" onClick={() => q.refetch()}>
            {t('common.retry', { defaultValue: 'Retry' })}
          </Button>
        </div>
      )}

      {!q.isLoading && !q.isError && items.length === 0 && (
        <div className={styles.empty}>
          {t('cc.register.empty', {
            defaultValue: 'No change requests yet. Propose a change to begin the GMP change-control cycle.',
          })}
        </div>
      )}

      {!q.isLoading && items.length > 0 && (
        <table className={styles.table} aria-label="Change control register">
          <thead>
            <tr>
              <th>{t('cc.col.ref', { defaultValue: 'Ref' })}</th>
              <th>{t('cc.col.title', { defaultValue: 'Title' })}</th>
              <th>{t('cc.col.type', { defaultValue: 'Type' })}</th>
              <th>{t('cc.col.origin', { defaultValue: 'Origin' })}</th>
              <th>{t('cc.col.status', { defaultValue: 'Status' })}</th>
              <th>{t('cc.col.created', { defaultValue: 'Created' })}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((cr) => (
              <tr
                key={cr.change_request_id}
                className={styles.dataRow}
                tabIndex={0}
                onClick={() => open(cr)}
                onKeyDown={(e) => { if (e.key === 'Enter') open(cr); }}
              >
                <td className={styles.mono}>{cr.change_request_id.slice(-8)}</td>
                <td>{cr.title}</td>
                <td>{cr.change_type?.replaceAll('_', ' ')}</td>
                <td>{cr.origin?.replaceAll('_', ' ')}</td>
                <td><span className={`${styles.chip} ${statusClass(cr.status)}`}>{cr.status?.replaceAll('_', ' ')}</span></td>
                <td>{cr.created_at_utc ? dateFmt.format(new Date(cr.created_at_utc)) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
